import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';

const CLOUDFRONT_URL = 'https://dkd2nnrprev6v.cloudfront.net';

// Define university data once in a central place
export const universities = [
  { 
    id: 'uach', 
    name: 'Universidad Autónoma de Chihuahua', 
    shortName: 'UACH',
    initials: 'UACH',
    location: 'Chihuahua, México',
    color: '#007A33',
    logo: `${CLOUDFRONT_URL}/uach.svg`
  },
  { 
    id: 'tec', 
    name: 'TEC de Monterrey', 
    shortName: 'TEC',
    initials: 'TEC',
    location: 'México',
    color: '#0072CE',
    logo: `${CLOUDFRONT_URL}/tec.svg`
  },
  { 
    id: 'ibero', 
    name: 'Universidad Iberoamericana', 
    shortName: 'IBERO',
    initials: 'IBERO',
    location: 'México',
    color: '#C8102E',
    logo: `${CLOUDFRONT_URL}/ibero.webp`
  },
  { 
    id: 'anahuac', 
    name: 'Universidad Anáhuac', 
    shortName: 'ANÁHUAC',
    initials: 'UAN',
    location: 'México',
    color: '#002855',
    logo: `${CLOUDFRONT_URL}/anahuac.svg`
  },
];

const UniversityContext = createContext();

export const UniversityProvider = ({ children }) => {
  const [selectedUniversity, setSelectedUniversity] = useState(() => {
    return localStorage.getItem('selectedUniversity') || '';
  });
  const [isChanging, setIsChanging] = useState(false);
  const changeTimeoutRef = useRef(null);

  // Find current university from selection
  const currentUniversity = universities.find(uni => uni.id === selectedUniversity) || null;

  useEffect(() => {
    if (selectedUniversity) {
      localStorage.setItem('selectedUniversity', selectedUniversity);
      
      // Only mark as having selected if it's a valid ID
      if (universities.some(uni => uni.id === selectedUniversity)) {
        localStorage.setItem('hasSelectedUniversity', 'true');
      }
    }
  }, [selectedUniversity]);

  // Debounced university change function
  const changeUniversity = useCallback((universityId) => {
    // If we're already changing, clear existing timeout
    if (changeTimeoutRef.current) {
      clearTimeout(changeTimeoutRef.current);
    }

    // Set changing state to true
    setIsChanging(true);
    
    // Set a timeout to prevent rapid changes
    changeTimeoutRef.current = setTimeout(() => {
      setSelectedUniversity(universityId);
      setIsChanging(false);
      changeTimeoutRef.current = null;
    }, 100); // 100ms debounce - reduced from 300ms for faster response
  }, []);

  return (
    <UniversityContext.Provider value={{ 
      selectedUniversity, 
      currentUniversity,
      universities,
      changeUniversity,
      isChanging,
      hasSelectedUniversity: !!selectedUniversity && universities.some(uni => uni.id === selectedUniversity)
    }}>
      {children}
    </UniversityContext.Provider>
  );
};

export const useUniversity = () => useContext(UniversityContext);

export default UniversityContext;
